import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export type StatusFilter = 'all' | 'down' | 'up' | 'paused';

/**
 * Search and a status filter, above the list.
 *
 * ## Why a page with three monitors still needs this
 *
 * It does not — but a page with three monitors becomes a page with thirty the moment a customer
 * connects their second and third domain, and each domain brings four checks. At that point finding
 * "the DNS record for example.com" means reading every row, and the whole promise of monitoring is
 * not having to look.
 *
 * The counts sit in the buttons because a filter that might match nothing should say so before it
 * is pressed, rather than leaving somebody staring at an empty list wondering if it is broken.
 */
export function MonitorFilters({
  search,
  onSearch,
  status,
  onStatus,
  counts,
}: {
  search: string;
  onSearch: (value: string) => void;
  status: StatusFilter;
  onStatus: (value: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}) {
  const options: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'down', label: 'Down' },
    { value: 'up', label: 'Up' },
    { value: 'paused', label: 'Paused' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[220px] flex-1">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="pl-9"
          aria-label="Search monitors by domain, name or check type"
        />
        {search !== '' && (
          <button
            type="button"
            onClick={() => onSearch('')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 p-1"
            aria-label="Clear search"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {options.map((option) => (
          <Button
            key={option.value}
            variant={status === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => onStatus(option.value)}
            // Nothing to filter to is still worth showing rather than hiding the control, so the
            // set of options does not shift about as monitors change state.
            disabled={counts[option.value] === 0 && option.value !== 'all'}
          >
            {option.label}
            <span className="ml-1.5 opacity-60">{counts[option.value]}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
